import { AppRoutes } from '@/enums/app-routes';
import { ShoppingCartReviewShowtime } from '@/types/shopping-cart';
import { buildLateNightLabel, formatDateTimeWithTimezone, getPreviousCalendarDate } from '@/utils/date-time-helper';
import { buildUrlWithParams } from '@/utils/url-params';
import { ShoppingCart } from '@cinemark/checkout-api-client';

const getDateKey = (date: Date, timeZone?: string): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: '2-digit',
    timeZone,
    year: 'numeric',
  }).formatToParts(date);
  const getPart = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find(part => part.type === type)?.value ?? '';

  return `${getPart('year')}-${getPart('month')}-${getPart('day')}`;
};

const getHour = (date: Date, timeZone?: string): number => {
  const hour = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    hourCycle: 'h23',
    timeZone,
  }).formatToParts(date).find(part => part.type === 'hour')?.value;

  return Number(hour ?? 0);
};

const formatTime = (date: Date, timeZone?: string): string =>
  new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    hour12: true,
    minute: '2-digit',
    timeZone,
  }).format(date).toLowerCase();

export const buildShoppingCartEditUrl = (showtime: ShoppingCartReviewShowtime): string => {
  return buildUrlWithParams(AppRoutes.TICKET_SEAT_MAP, {
    cart: true,
    cinemarkMovieId: showtime.cinemarkMovieId,
    linkedShowtimeId: showtime.linkedShowtimeId,
    showtime: formatDateTimeWithTimezone(showtime.displayShowtime, showtime.theaterTimeZone),
    showtimeId: showtime.cartShowtime.showtimeId,
    theaterId: showtime.cartShowtime.theaterId,
  });
};

export const formatShoppingCartShowtime = (
  showtime: Date,
  timeZone?: string,
  now: Date = new Date()
): string => {
  const businessDate = getHour(showtime, timeZone) < 6
    ? getPreviousCalendarDate(showtime, timeZone)
    : new Date(`${getDateKey(showtime, timeZone)}T00:00:00Z`);

  if (getDateKey(businessDate, 'UTC') === getDateKey(now, timeZone)) {
    return `Today at ${formatTime(showtime, timeZone)}`;
  }

  const dateText = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
    weekday: 'long',
  }).format(businessDate);

  return `${dateText} at ${formatTime(showtime, timeZone)}`;
};

export const getShoppingCartAfterMidnightLabel = (showtime: Date, timeZone?: string): string | undefined =>
  getHour(showtime, timeZone) < 6 ? buildLateNightLabel(undefined, showtime, timeZone) : undefined;

export const hasShoppingCartProducts = (cart: ShoppingCart | null): boolean => {
  if (!cart) {
    return false;
  }

  return cart.showtimes.some(showtime => showtime.tickets.length || showtime.concessions.length)
    || cart.familyEntertainmentCenterConcessions.length > 0
    || cart.giftCards.length > 0
    || cart.passes.length > 0
    || cart.superSavers.length > 0
    || cart.subscriptions.length > 0
    || cart.subscriptionAddOns.length > 0;
};

export const removeShowtimeFromCart = (cart: ShoppingCart, showtimeId: number): ShoppingCart => ({
  ...cart,
  showtimes: cart.showtimes.filter(showtime => showtime.showtimeId !== showtimeId),
});
